import React from 'react';
import { FlexRow } from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import {
  AccountInfoResult,
  ContractTypeId,
  ContractTypes,
  Deploy,
} from '@block-explorer/entities';
import Skeleton from 'react-loading-skeleton';
import { ACCOUNT_DETAILS_PATH } from '../../router/paths';
import { AccountInfoIcon } from '../account-info-icon/account-info-icon';
import { buildRouterPath } from '../../router/utils';
import {
  AuctionActionMessage,
  FtTokenActionMessage,
  NftTokenActionMessage,
  CsprMarketActionMessage,
  AssociatedKeysActionMessage,
  DefaultActionMessage,
  TransferActionMessage,
  CsprFunActionMessage,
} from './action-messages';
import { useAccountInfoContext } from '../../services/account-data-service';
import { deriveAccountInfo } from '../../utils/account.tsx';
import {
  ExecutionTypesMap,
  isTransferDeploy,
  isWASMTransaction,
} from '../../utils/contract.tsx';

const WASMActionMessage = ({
  callerHash,
  deployType,
}: {
  callerHash: string;
  deployType: string;
}) => {
  const { getAccountInfo } = useAccountInfoContext();
  const accountInfo = getAccountInfo<AccountInfoResult>(callerHash);

  const accountInfoDetails = deriveAccountInfo(
    accountInfo?.account_info || accountInfo?.centralized_account_info
  );
  const logo = accountInfoDetails && accountInfoDetails?.logo;
  const csprName = accountInfo?.cspr_name;

  return (
    <FlexRow align={'baseline'} itemsSpacing={4}>
      <BodyText monotype size={3}>
        {deployType}
      </BodyText>
      {callerHash ? (
        <FlexRow itemsSpacing={6} align={'center'}>
          <BodyText size={3} variation={'darkGray'}>
            by{' '}
          </BodyText>{' '}
          <AccountInfoIcon
            publicKey={callerHash}
            logo={logo}
            csprName={csprName}
            path={buildRouterPath(
              ACCOUNT_DETAILS_PATH,
              { id: callerHash },
              undefined
            )}
          />{' '}
        </FlexRow>
      ) : null}
    </FlexRow>
  );
};

export interface TableDataActionMessageProps {
  deployData: Deploy;
  loading: boolean;
  isTransfer?: boolean;
}

export const TableDataActionMessage = ({
  deployData,
  loading,
}: TableDataActionMessageProps) => {
  const {
    callerPublicKey,
    callerHash,
    executionTypeId,
    contractPackage,
    contractHash,
    contractPackageHash,
    entryPoint,
    args,
  } = deployData;

  const deployType = ExecutionTypesMap[executionTypeId];
  const isWasm = isWASMTransaction(executionTypeId); // executionTypeId !== WASM_DEPLOY;
  const isTransfer = isTransferDeploy(contractHash, entryPoint?.name);
  const isCSPRFun =
    args?.csprfun_contract_hash_key && args?.token_to_trade_contract_hash_key;

  const contractTypeId =
    contractPackage?.latest_version_contract_type_id ||
    contractPackage?.contract_type_id;

  if (loading || !deployData) {
    return (
      <BodyText size={3}>
        <Skeleton />
      </BodyText>
    );
  }

  if (isTransfer) {
    return <TransferActionMessage deploy={deployData} />;
  }

  if (isWasm && !isCSPRFun) {
    return (
      <WASMActionMessage
        callerHash={callerPublicKey || callerHash}
        deployType={deployType}
      />
    );
  }

  if (isCSPRFun) {
    return <CsprFunActionMessage deploy={deployData} />;
  }

  if (!contractPackage) {
    return (
      <BodyText monotype size={3}>
        N/A
      </BodyText>
    );
  }

  if (contractHash === config.auction_manager_contract_hash) {
    return <AuctionActionMessage deploy={deployData} />;
  }
  if (contractHash === config.associated_keys_contract_hash) {
    return <AssociatedKeysActionMessage deploy={deployData} />;
  }
  if (
    contractPackageHash === config.cspr_market_v1_contract_package_hash ||
    contractPackageHash === config.cspr_market_contract_package_hash
  ) {
    return <CsprMarketActionMessage deploy={deployData} />;
  }
  if (
    contractTypeId === ContractTypeId.CEP78Nft ||
    contractTypeId === ContractTypeId.CEP47Nft ||
    contractTypeId === ContractTypeId.CEP95Nft ||
    contractTypeId === ContractTypeId.CustomCEP78Nft ||
    contractTypeId === ContractTypeId.CustomCEP47Nft
  ) {
    return <NftTokenActionMessage deploy={deployData} />;
  }
  if (
    contractTypeId === ContractTypes.CustomCep18 ||
    contractTypeId === ContractTypes.Cep18
  ) {
    return <FtTokenActionMessage deploy={deployData} />;
  }

  return <DefaultActionMessage deploy={deployData} />;
};
